import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {MachineLicense} from '@/entities/machine/types';
import {
  licenseState,
  seatState,
  type LicenseState,
} from '@/entities/machine/license';
import {Banner} from '@/shared/ui/banner';
import {HelpNote} from '@/shared/ui/explain';
import {AppText} from '@/shared/ui/text';
import {formatDate} from '@/shared/lib/format';
import {spacing} from '@/shared/ui/theme';

/**
 * Лицензия машины — и предупреждение, когда она заканчивается.
 *
 * Показывается там, где лицензия у кабинета вообще есть на руках: в карточке машины
 * при подключении. Почему только там — разобрано в `entities/machine/license.ts`:
 * лицензионные списки закрыты доменом `system`, а номер машины, единственный ключ к
 * self-service маршруту, в списке устройств не приезжает.
 *
 * Спокойное состояние тоже печатается, не только тревожное. «До 12.09.2027» строкой
 * под номером машины стоит дёшево и отвечает на вопрос, который иначе задают в
 * поддержку; а когда однажды покажется предупреждение, оно окажется в том же месте,
 * где раньше стоял спокойный текст, — а не выскочит из ниоткуда.
 *
 * Даты `valid_until` может не быть (бессрочная) и она может не разобраться — оба
 * случая не ошибка и оба названы словом, а не прочерком.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(2)},
  line: {gap: 2},
});

/** Что сказать про срок — одной строкой, на языке владельца, а не сервера. */
function summaryLine(
  state: LicenseState,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  switch (state.kind) {
    case 'missing':
      return t('license.missing');
    case 'suspended':
      return t('license.suspended');
    case 'perpetual':
      return t('license.perpetual');
    case 'expired':
      return state.until
        ? t('license.expiredOn', {date: formatDate(Date.parse(state.until))})
        : t('license.expired');
    case 'expiring':
      // Ноль дней — это «сегодня», а не «через 0 дней»: русское правило множественного
      // числа отправляет ноль в ту же форму, что и пять, и получается фраза, которую
      // никто не говорит вслух в день, когда терминал перестаёт работать.
      return state.daysLeft === 0
        ? t('license.expiringToday')
        : t('license.expiringIn', {
            count: state.daysLeft,
            date: formatDate(Date.parse(state.until)),
          });
    case 'active':
      return t('license.validUntil', {
        date: formatDate(Date.parse(state.until)),
      });
  }
}

export function LicenseNotice({
  license,
  /** Пинуется вызывающим, чтобы «сегодня» не сдвинулось между рендерами. */
  now,
  testID,
}: {
  license: MachineLicense | null | undefined;
  now?: number;
  testID?: string;
}) {
  const {t} = useTranslation();
  const state = licenseState(license, now);
  const seats = seatState(license);
  const line = summaryLine(state, t);

  // Три тона на шесть состояний: «пора действовать» краснеет, «скоро придётся» —
  // жёлтое, остальное вообще не баннер, а строка. Баннер на спокойном состоянии
  // обесценивает баннер на тревожном.
  const tone =
    state.kind === 'expired' || state.kind === 'missing'
      ? 'error'
      : state.kind === 'expiring' || state.kind === 'suspended'
      ? 'warning'
      : null;

  const seatLine =
    seats && !seats.unlimited
      ? seats.full
        ? t('license.seatsFull', {max: seats.max})
        : t('license.seats', {used: seats.used, max: seats.max})
      : null;

  return (
    <View style={styles.root} testID={testID ?? 'license-notice'}>
      {tone ? (
        <Banner
          tone={tone}
          icon="shield"
          title={line}
          message={
            state.kind === 'expiring' || state.kind === 'expired'
              ? t('license.renewHint')
              : undefined
          }
        />
      ) : (
        <View style={styles.line}>
          <AppText variant="eyebrow">{t('license.title')}</AppText>
          <AppText variant="body">{line}</AppText>
        </View>
      )}

      {seatLine ? <HelpNote text={seatLine} /> : null}
    </View>
  );
}
